import React, { useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import { getShiftDate } from "../../util/dateTimeFormatter";
import { makeStyles } from "@mui/styles";
import { fetchLatestBidLineEventsByReferenceNumber } from "../../api/bid";
import { convertFullDayToShort } from "../../util/bid/bidFormUtils";
import ShiftBidPDFExport from "../../pages/bid/ShiftBidPDFExport";
import { filterAndSortSelectedBidLines } from "../../util/bid/filterBids";
import CloseIcon from "@mui/icons-material/Close";
import { MY_BID_RESULTS_DIALOG_MESSAGE } from "../../util/messageConstants";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { isAssignmentUsedToDisplay } from "../../util/bid/shiftBidUtils";

const useStyles = makeStyles((theme) => ({
  circularProgress: {
    marginBottom: "10px",
  },
  bidLine: {
    color: theme.palette.primary.swaBlue,
    fontWeight: "bold",
  },
  bidScheduleDay: {
    fontWeight: "bold",
  },
  bidSchedules: {
    marginTop: "10px",
  },
  closeIcon: {
    alignSelf: "flex-end",
    color: theme.palette.grey[500],
    marginRight: "20px",
    marginTop: "5px",
  },
}));

const MyBidResultsDialog = (props) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);
  const [awardedBidLine, setAwardedBidLine] = useState(null);
  const [bidLines, setBidLines] = useState(null);

  useEffect(() => {
    if (isLoading) {
      fetchAwardedBidLine();
    }
    // eslint-disable-next-line
  }, [isLoading]);

  const fetchAwardedBidLine = () => {
    fetchLatestBidLineEventsByReferenceNumber(
      props.selectedAwardedBidChoiceData.eid,
      props.selectedAwardedBidChoiceData.referenceNumber
    )
      .then((res) => res.json())
      .then((response) => {
        if (response?.events) {
          const initFtPtBidLines = response.events.map((event) => {
            event.id = event.lineNumber;
            return event;
          });
          setBidLines(initFtPtBidLines);

          for (const bidLine of response.events) {
            if (bidLine.awardedEid === props.selectedAwardedBidChoiceData.eid) {
              setAwardedBidLine(bidLine);
              break;
            }
          }
          setIsLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
      });
  };

  const renderMyBidResultsDialogTitle = () => {
    let title = `${props.selectedAwardedBidChoiceData.station} | ${
      props.selectedAwardedBidChoiceData.workgroup
    } | ${getShiftDate(props.selectedAwardedBidChoiceData?.schedStartDate)} - ${getShiftDate(
      props.selectedAwardedBidChoiceData?.schedEndDate
    )} ${props.selectedAwardedBidChoiceData.bidType}`;
    return title;
  };

  const renderMyBidResultsDialogContent = () => {
    if (isLoading) {
      return <CircularProgress className={classes.circularProgress} />;
    } else if (!awardedBidLine) {
      return <div className={classes.bidLine}>{MY_BID_RESULTS_DIALOG_MESSAGE}</div>;
    } else {
      return (
        <>
          <Grid item className={classes.bidLine}>
            Bid Line {awardedBidLine?.lineNumber}
          </Grid>
          {renderAwardedBidSchedule()}
        </>
      );
    }
  };

  const getLastSubmittedTimestamp = () => {
    return props.selectedAwardedBidChoiceData.createdAt ? Date.parse(props.selectedAwardedBidChoiceData.createdAt) : "";
  };

  const renderAwardedBidSchedule = () => {
    return (
      <Grid container item className={classes.bidSchedules}>
        {awardedBidLine?.shifts.map((shift) => (
          <Grid container item direction="row" key={shift.day}>
            <Grid item xs={3} className={classes.bidScheduleDay}>
              {convertFullDayToShort(shift.day)}
            </Grid>
            <Grid item xs={5}>
              {shift.isOff ? "OFF" : `${shift.start} - ${shift.end}`}
            </Grid>
            <Grid item xs={4}>
              {isAssignmentUsedToDisplay(props.selectedAwardedBidChoiceData)
                ? !shift.isOff && shift.assignment
                : !shift.isOff && shift.position}
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Dialog
      PaperProps={{
        sx: {
          width: "350px",
          maxWidth: "90vw",
          margin: "auto",
          whiteSpace: "pre-line",
        },
      }}
      open={!!props.selectedAwardedBidChoiceData}
      onClose={props.onClickDialogClose}
      data-testid="my-bid-results-dialog"
      TransitionComponent={SlideUpTransition}
      keepMounted
    >
      <IconButton aria-label="close" onClick={props.onClickDialogClose} className={classes.closeIcon}>
        <CloseIcon />
      </IconButton>
      <DialogTitle>{renderMyBidResultsDialogTitle()}</DialogTitle>
      <DialogContent>
        <Grid container direction="column">
          {renderMyBidResultsDialogContent()}
        </Grid>
        <ShiftBidPDFExport
          isEmployeeAwardExport={true}
          selectedBidLines={
            bidLines ? filterAndSortSelectedBidLines(bidLines, props.selectedAwardedBidChoiceData.choices) : null
          }
          lastSubmittedTimestamp={getLastSubmittedTimestamp()}
          bidChoiceData={props.selectedAwardedBidChoiceData}
          isMyBidResultsDialogLoading={isLoading}
          bidLines={bidLines}
          useAssignmentForEmp={isAssignmentUsedToDisplay(props.selectedAwardedBidChoiceData)}
        />
      </DialogContent>
    </Dialog>
  );
};

MyBidResultsDialog.propTypes = {
  selectedAwardedBidChoiceData: PropTypes.object.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
};

export default MyBidResultsDialog;
